import type { ObservabilityConfig } from './types';

/**
 * Default configuration values
 */
export const DEFAULT_CONFIG: Required<Omit<ObservabilityConfig, 'dsn' | 'release'>> &
  Pick<ObservabilityConfig, 'dsn' | 'release'> = {
  dsn: undefined,
  environment: 'development',
  release: undefined,
  tracesSampleRate: 0.1,
  profilesSampleRate: 0.3,
  enableWebVitals: true,
  debug: false,
  disabled: false,
};

/**
 * Merge user config with defaults
 */
export function mergeConfig(
  config: ObservabilityConfig
): Required<Omit<ObservabilityConfig, 'dsn' | 'release'>> &
  Pick<ObservabilityConfig, 'dsn' | 'release'> {
  return {
    ...DEFAULT_CONFIG,
    ...config,
  };
}

/**
 * Check if we should skip initialization
 */
export function shouldSkipInit(config: ObservabilityConfig): { skip: boolean; reason?: string } {
  if (config.disabled) {
    return { skip: true, reason: 'Observability is disabled via config' };
  }

  // Skip in development/test unless debug mode is enabled
  if (typeof window !== 'undefined') {
    // Check for Vite env
    const isDev = (import.meta as { env?: { DEV?: boolean; MODE?: string } }).env?.DEV;
    const mode = (import.meta as { env?: { DEV?: boolean; MODE?: string } }).env?.MODE;

    if (isDev && !config.debug) {
      return { skip: true, reason: 'Skipping in development mode (set debug: true to enable)' };
    }

    if (mode === 'test') {
      return { skip: true, reason: 'Skipping in test mode' };
    }
  }

  if (!config.dsn) {
    return { skip: true, reason: 'No DSN provided' };
  }

  return { skip: false };
}
